import { forEach } from "lodash";
import { GazetteerRecordParsed } from "../services/GazetteerService";
import { getValuesWithNewLines } from "../utils/getValuesWithNewLines";

export class AddressHelper {
    static stringWithDash = (str: string | null | undefined) => (str ? `-${str}` : "");
    static getNumberRange(gazetteerRecord: any, paosao: "PAO" | "SAO"): string {
        const startText = (gazetteerRecord[paosao + "_START_NUMBER"] || "") + (gazetteerRecord[paosao + "_START_SUFFIX"] || "");
        const endText = (gazetteerRecord[paosao + "_END_NUMBER"] || "") + (gazetteerRecord[paosao + "_END_SUFFIX"] || "");
        if (endText) {
            return startText + "-" + endText;
        } else {
            return startText;
        }
    }
    static getAddressLinesFromGazetteerRecord(gazetteerRecord: GazetteerRecordParsed): IAddressLines {
        const lines: any[] = [];
        lines.push(gazetteerRecord.ORGANISATION);
        lines.push(gazetteerRecord.SAO_TEXT);
        const saoRange = this.getNumberRange(gazetteerRecord, "SAO");
        if (gazetteerRecord.PAO_TEXT) {
            if (saoRange) {
                lines.push(saoRange + " " + gazetteerRecord.PAO_TEXT);
            } else {
                lines.push(gazetteerRecord.PAO_TEXT);
            }
        }

        const streetDescriptor = gazetteerRecord.STREET_DESCRIPTOR || "";
        const paoRange = this.getNumberRange(gazetteerRecord, "PAO");
        if (paoRange) {
            if (!gazetteerRecord.PAO_TEXT && saoRange) {
                lines.push((saoRange + ", " + paoRange + " " + streetDescriptor).trim());
            } else {
                lines.push((paoRange + " " + streetDescriptor).trim());
            }
        } else {
            lines.push(streetDescriptor);
        }
        let townName = "";
        if (gazetteerRecord.LOCALITY_NAME) {
            lines.push(gazetteerRecord.LOCALITY_NAME);
        } else {
            townName = gazetteerRecord.TOWN_NAME;
            lines.push(gazetteerRecord.TOWN_NAME);
        }

        if (gazetteerRecord.POST_TOWN && (!townName || gazetteerRecord.POST_TOWN.toUpperCase() !== townName.toUpperCase())) {
            lines.push(gazetteerRecord.POST_TOWN);
        }

        if (gazetteerRecord.POSTCODE) {
            lines.push(gazetteerRecord.POSTCODE);
        }
        const nonEmptyLines = lines.filter((line) => line);
        const singleLine = nonEmptyLines.join(", ");
        const multiLine = getValuesWithNewLines(nonEmptyLines);
        const line1 = nonEmptyLines[0] || "";
        const line2 = AddressHelper.removeTokens(nonEmptyLines[1] || "", [townName, gazetteerRecord.POST_TOWN, gazetteerRecord.POSTCODE]);
        const line3 = AddressHelper.removeTokens(nonEmptyLines[2] || "", [townName, gazetteerRecord.POST_TOWN, gazetteerRecord.POSTCODE]);
        return { lines: nonEmptyLines, singleLine, multiLine, line1, line2, line3 };
    }
    static removeTokens(str: string, tokens: (string | undefined)[]) {
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if (token) {
                if (str.toLowerCase() === token.toLowerCase()) {
                    return "";
                }
            }
        }
        return str.trim();
    }

    static old(gazetteerRecord: GazetteerRecordParsed): any {
        if (gazetteerRecord) {
            let {
                STREET_DESCRIPTOR,
                SAO_START_NUMBER,
                SAO_END_NUMBER,
                SAO_START_SUFFIX,
                SAO_END_SUFFIX,
                PAO_START_NUMBER,
                PAO_START_SUFFIX,
                PAO_END_NUMBER,
                PAO_END_SUFFIX,
                PAO_TEXT,
                ORGANISATION,
                SAO_TEXT,
                LOCALITY_NAME,
            } = gazetteerRecord;

            if (!STREET_DESCRIPTOR) {
                STREET_DESCRIPTOR = "";
            }
            if (!SAO_START_NUMBER) {
                SAO_START_NUMBER = "";
            }
            if (!SAO_END_NUMBER) {
                SAO_END_NUMBER = "";
            }
            if (!SAO_START_SUFFIX) {
                SAO_START_SUFFIX = "";
            }
            if (!SAO_END_SUFFIX) {
                SAO_END_SUFFIX = "";
            }
            if (!PAO_START_NUMBER) {
                PAO_START_NUMBER = "";
            }
            if (!PAO_START_SUFFIX) {
                PAO_START_SUFFIX = "";
            }
            if (!PAO_END_NUMBER) {
                PAO_END_NUMBER = "";
            }
            if (!PAO_END_SUFFIX) {
                PAO_END_SUFFIX = "";
            }
            if (!PAO_TEXT) {
                PAO_TEXT = "";
            }
            if (!ORGANISATION) {
                ORGANISATION = "";
            }
            if (!SAO_TEXT) {
                SAO_TEXT = "";
            }
            if (!LOCALITY_NAME) {
                LOCALITY_NAME = "";
            }

            const lines = [];
            const organisationUpperCased = ORGANISATION.toUpperCase();
            const saoTextUpperCased = SAO_TEXT.toUpperCase();
            const paoTextUpperCased = PAO_TEXT.toUpperCase();
            const sao = `${SAO_START_NUMBER}${SAO_START_SUFFIX}${AddressHelper.stringWithDash(SAO_END_NUMBER)}${SAO_END_SUFFIX}`.trim();
            const pao = `${PAO_START_NUMBER}${PAO_START_SUFFIX}${AddressHelper.stringWithDash(PAO_END_NUMBER)}${PAO_END_SUFFIX}`.trim();

            const addressLine1 = `${organisationUpperCased} ${saoTextUpperCased}`.trim();
            const addressLine2 = `${sao} ${paoTextUpperCased}`.trim();
            const defaultLine3 = `${pao} ${STREET_DESCRIPTOR}`.trim();

            addressLine1 && lines.push(addressLine1);
            addressLine2 && lines.push(addressLine2);
            defaultLine3 && lines.push(defaultLine3);
            if (lines.length < 3 && LOCALITY_NAME) {
                lines.push(LOCALITY_NAME);
            }

            const [line1, line2, line3] = lines;

            return { line1, line2, line3 };
        }
        return { line1: "", line2: "", line3: "" };
    }
}

export interface IAddressLines {
    line1: string;
    line2: string;
    line3: string;
    lines: string[];
    singleLine: string;
    multiLine: string;
}
